import { receiveNewKeyword, removeKeyword } from 'actions/identifier';
import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { LanguageMatcherType, UUID } from 'types';
import AnnotatorMultiSelectDropdown from '../AnnotatorMultiSelectDropdown';
import LanguageMatcherList from '../LanguageMatcherList';

type Option = { value: string; label: string; disabled?: boolean };

type Props = {
  label: string;
  languageMatcherId: UUID;
  parentType: LanguageMatcherType;
  field: string;
  options: Option[];
  value: string[] | undefined;
  widthClassName?: string;
  compareMode?: boolean;
  shouldDisable?: boolean;
};

const MultiSelectField: React.FC<Props> = ({
  label,
  languageMatcherId,
  parentType,
  field,
  options,
  value,
  widthClassName = 'w-60',
  compareMode = false,
  shouldDisable = false,
}) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);

  const selected = useMemo(() => (Array.isArray(value) ? value : []), [value]);

  const toggle = (item?: string | boolean): void => {
    if (typeof item !== 'string') return;
    if (selected.includes(item)) {
      dispatch(
        removeKeyword({
          keyword: item,
          languageMatcherId,
          partOfSpeech: field,
        })
      );
    } else {
      dispatch(
        receiveNewKeyword({
          languageMatcherId,
          parentType,
          data: {
            key: field,
            value: [item],
          },
        })
      );
      dispatch(
        receiveNewKeyword({
          languageMatcherId,
          parentType,
          data: { key: 'saved', value: false },
        })
      );
    }
  };

  return (
    <div className={`flex flex-col gap-1 ${widthClassName}`}>
      <span className="text-body">{label}</span>
      <AnnotatorMultiSelectDropdown
        onChange={toggle}
        options={options.map((o) => ({
          ...o,
          disabled: compareMode || shouldDisable || o.disabled,
        }))}
        selectedItems={selected}
        dataTestid={`${String(field)}-multi`}
        className="w-full"
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      />
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selected.map((t) => (
            <LanguageMatcherList
              key={t}
              id={languageMatcherId}
              keyword={t}
              type={field}
              compareMode={compareMode}
              shouldDisable={shouldDisable}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MultiSelectField;
